import useTransactionsApi from '../api/useTransactionsApi';
import { useApiErrorSnackbar } from '../common/useApiErrorSnackbar';
import {
  type DropdownButtonType,
  type SnackbarMessage,
  type UseTransactionActionsDeps
} from '@/types';
import { useTransactionsStore } from '#imports';
import { useSupportWindowUtils } from '@/utils/supportWindowUtils';
import { useWalletsStore } from '~/stores/wallets/wallets-store';

export const useTransactionActions = ({
  forms,
  getTransactionInfos
}: UseTransactionActionsDeps) => {
  const { $i18n } = useNuxtApp();
  const { t, n } = $i18n;
  const route = useRoute();
  const { postSendPaymentP2P, postRefundPayment } = useTransactionsApi();
  const { add } = useSnackbar();
  const { notifyApiError } = useApiErrorSnackbar();
  const { openSupportWindowHandler } = useSupportWindowUtils();
  const transactionsStore = useTransactionsStore();
  const { showSendPaymentToAnotherWalletModal } = transactionsStore;
  const {
    sendPaymentModal,
    refundPaymentModal,
    chargeBackModal,
    SimulateMoneyInModal,
    captureTransactionModal,
    transactionInfos
  } = storeToRefs(transactionsStore);
  const { userInfos } = storeToRefs(useWalletsStore());

  const {
    initialPaymentBody,
    sendPaymentBody,
    currency,
    formatSendPaymentBody,
    formatRefundPaymentBody
  } = forms;

  const transactionId = computed(() =>
    Array.isArray(route.params.id) ? route.params.id[0] : route.params.id
  );
  const partnerId = computed(() => route.params.partner);

  const toggleSendPaymentModal = () => {
    sendPaymentBody.value = { ...initialPaymentBody.value };
    sendPaymentModal.value = !sendPaymentModal.value;
  };

  const toggleRefundPaymentModal = () => {
    refundPaymentModal.value = !refundPaymentModal.value;
  };

  const toggleChargeBackModal = () => {
    chargeBackModal.value = !chargeBackModal.value;
  };

  const toggleSimulateMoneyInModal = () => {
    SimulateMoneyInModal.value = !SimulateMoneyInModal.value;
  };

  const toggleCaptureTransactionModal = () => {
    captureTransactionModal.value = !captureTransactionModal.value;
  };

  const toggleConfirmActionModal = () => {
    transactionsStore.openConfirmActionModal = !transactionsStore.openConfirmActionModal;
  };

  const showSendPaymentModal = () => {
    const isSC = route.path.split('/')[1] === 'my-wallet';
    showSendPaymentToAnotherWalletModal({
      icon: !isSC ? (userInfos.value.isCompany ? 'MyAccount' : 'Person') : 'StoreFront',
      senderName: !isSC
        ? userInfos.value.isCompany
          ? (userInfos.value.companyName ?? '')
          : `${userInfos.value?.clientFirstName} ${userInfos.value?.clientLastName}`
        : 'My wallet (SC)',
      walletExtId: !isSC ? String(userInfos.value?.wallet) : 'SC'
    });
  };

  const handleRefundPayment = async (emit: Function, isValid: boolean) => {
    if (isValid) {
      try {
        const formatedBody = formatRefundPaymentBody();
        await postRefundPayment(formatedBody);
        add({
          type: 'success',
          message: `${t('transactions.details.transactionActionModal.successRefundMoneyIn', {
            transactionId: transactionId.value
          })}: ${n(+formatedBody.AmountToRefund, { style: 'currency', currency: currency.value })}`
        } as SnackbarMessage);
        toggleRefundPaymentModal();
        emit();
      } catch (error: unknown) {
        notifyApiError(error);
      }
    }
  };

  const handleSendPayment = async (emit: Function, isValid: boolean) => {
    if (isValid) {
      try {
        const formatedBody = formatSendPaymentBody();
        const data = await postSendPaymentP2P(formatedBody);
        add({
          type: 'success',
          message: t('transactions.successTransaction', { transactionId: data.Id }),
          link: `/${partnerId.value}/transactions/p2p/${data.Id}/details`
        } as SnackbarMessage);
        toggleSendPaymentModal();
        emit();
      } catch (error: unknown) {
        notifyApiError(error);
      }
    }
  };

  const openSupportWindowForTransaction = async () => {
    if (!transactionInfos.value) {
      await getTransactionInfos();
    }

    if (!transactionInfos.value) {
      return;
    }

    const transactionInfo = transactionInfos.value;

    const supportSubject = `[PAYMENT]-${transactionInfo.walletIntId}-${transactionInfo.id}`;
    const supportCategory = 'transaction__money_in__p2p__money_out_';

    const supportExternalId = transactionInfo.walletExtId ?? 'undefined';
    await openSupportWindowHandler(
      supportSubject,
      supportCategory,
      supportExternalId,
      transactionInfo.walletIntId
    );
  };

  const transactionDetailsActions = computed<DropdownButtonType[]>(() => [
    {
      text: t('wallets.details.newSupportTicket'),
      iconPosition: 'left',
      leftIcon: 'Support',
      theme: 'ghost',
      action: () => openSupportWindowForTransaction(),
      permission: true
    }
  ]);

  return {
    toggleSendPaymentModal,
    toggleRefundPaymentModal,
    toggleChargeBackModal,
    toggleSimulateMoneyInModal,
    toggleCaptureTransactionModal,
    toggleConfirmActionModal,
    showSendPaymentModal,
    handleRefundPayment,
    handleSendPayment,
    openSupportWindowForTransaction,
    transactionDetailsActions
  };
};
